import { useEffect, useRef, useState } from "react";
import { Crop, ImageUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { mediaAbs } from "@/lib/media";
import { frameStyle } from "@/lib/framing";
import { FramingEditor } from "@/components/crm/admin/FramingEditor";
import { Button } from "@/components/ui/button";

const ACCEPT = "image/jpeg,image/png,image/webp";

/** "Subir imagen desde el dispositivo": picker + drag&drop (desktop), camera/gallery (mobile). onChange(url, defaultFraming). */
export const ImageUploader = ({ value, onChange, kind = "service", testid = "image-upload", aspect = "aspect-[16/10]",
  framing, onFramingChange, targets, previews, renderCard }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [editing, setEditing] = useState(false);
  const pending = useRef(null);
  const discardPending = () => {
    if (pending.current) api.delete(`/admin/media/${pending.current.id}`).catch(() => {});
    pending.current = null;
  };
  const set = (url, id = null, fr = null) => { discardPending(); pending.current = id ? { id, url } : null; onChange(url, fr); };
  useEffect(() => { if (pending.current && value !== pending.current.url) discardPending(); }, [value]); // preset/URL picked elsewhere
  const upload = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) { toast.error("Formato no válido. Usa JPG, PNG o WebP"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("La imagen supera 8 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(data.url, data.id, data.framing);
      toast.success("Imagen subida · guarda para aplicarla");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
           onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
           onClick={() => !busy && ref.current.click()} role="button" tabIndex={0}
           className={`relative ${aspect} cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition-colors ${drag ? "border-[#1E6FD9] bg-sky-50" : "border-slate-200 bg-[#F5F7FA] hover:border-[#1E6FD9]/60"}`}
           data-testid={`${testid}-dropzone`}>
        {value ? <img src={mediaAbs(value)} alt="Vista previa" className="h-full w-full object-cover" style={frameStyle(framing?.[targets?.[0]?.key])} data-testid={`${testid}-preview`} /> : (
          <div className="grid h-full place-items-center p-4 text-center text-sm text-slate-500">
            <span><ImageUp className="mx-auto mb-2 h-6 w-6 text-[#1E6FD9]" />Subir imagen desde el dispositivo<span className="block text-xs text-slate-400">Arrastra aquí o toca para elegir · JPG, PNG o WebP · máx. 8 MB</span></span>
          </div>
        )}
        {busy && <div className="absolute inset-0 grid place-items-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-[#1E6FD9]" /></div>}
      </div>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-input`} />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current.click()} className="rounded-lg" data-testid={`${testid}-button`}>
          <ImageUp className="mr-1.5 h-4 w-4" /> {value ? "Reemplazar imagen" : "Subir imagen desde el dispositivo"}
        </Button>
        {value && onFramingChange && (
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setEditing(true)} className="rounded-lg" data-testid={`${testid}-frame`}>
            <Crop className="mr-1.5 h-4 w-4" /> Ajustar encuadre
          </Button>
        )}
        {value && <Button type="button" variant="outline" size="sm" onClick={() => set("")} className="rounded-lg" data-testid={`${testid}-remove`}><Trash2 className="mr-1.5 h-4 w-4" /> Quitar</Button>}
      </div>
      {onFramingChange && value && (
        <FramingEditor open={editing} src={value} value={framing || {}} targets={targets} previews={previews} renderCard={renderCard}
                       onSave={onFramingChange} onClose={() => setEditing(false)} />
      )}
    </div>
  );
};
